// Hook to fetch and apply tenant branding (CSS variables + font).
// Called from TenantShell after authentication.

import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { brandingApi, SUPPORTED_FONTS, type BrandingResponse } from '@/lib/api/branding'
import { queryKeys } from '@/lib/api/queryKeys'

/** Map of branding config colour keys to CSS variable names. */
const COLOR_TO_CSS: Record<string, string> = {
  headerBackground: '--header-background',
  headerForeground: '--header-foreground',
  sidebarBackground: '--sidebar-background',
  sidebarForeground: '--sidebar-foreground',
  footerBackground: '--footer-background',
  footerForeground: '--footer-foreground',
  background: '--background',
  foreground: '--foreground',
  bodyForeground: '--body-foreground',
  border: '--border',
  card: '--card',
  cardForeground: '--card-foreground',
  cardHeaderForeground: '--card-header-foreground',
  buttonBackground: '--primary',
  buttonForeground: '--primary-foreground',
  gridAppetiteIn: '--grid-appetite-in',
  gridAppetiteOut: '--grid-appetite-out',
  gridNodeInherent: '--grid-node-inherent',
  gridNodeResidual: '--grid-node-residual',
  gridNodeTarget: '--grid-node-target',
}

/** Map of font IDs to CSS font-family values. */
const FONT_FAMILIES: Record<string, string> = Object.fromEntries(
  SUPPORTED_FONTS.map((f) => [f.id, f.family]),
)

/**
 * Apply branding config to the document root as CSS custom properties.
 * Only sets properties that are defined in the config — unset values
 * fall back to platform defaults from index.css.
 */
function applyBranding(config: BrandingResponse): void {
  const root = document.documentElement

  // Apply colours
  for (const [key, cssVar] of Object.entries(COLOR_TO_CSS)) {
    const value = config.colors[key as keyof typeof config.colors]
    if (value) {
      root.style.setProperty(cssVar, value)
    } else {
      // Remove override so platform default applies
      root.style.removeProperty(cssVar)
    }
  }

  // Apply font
  const fontFamily = FONT_FAMILIES[config.fontId] ?? FONT_FAMILIES['inter'] ?? 'Inter, sans-serif'
  root.style.setProperty('--font-family', fontFamily)
}

/** Remove all branding overrides (revert to platform defaults). */
function clearBranding(): void {
  const root = document.documentElement
  for (const cssVar of Object.values(COLOR_TO_CSS)) {
    root.style.removeProperty(cssVar)
  }
  root.style.removeProperty('--font-family')
}

/**
 * Hook that fetches branding config and applies it as CSS variables.
 * Returns the branding data for use in components (e.g., logo rendering).
 */
export function useBranding() {
  const api = useApi()
  const { slug } = useTenant()
  const bApi = brandingApi(api)

  const { data: branding } = useQuery({
    queryKey: queryKeys.tenantSettings.branding(slug),
    queryFn: () => bApi.get(),
    staleTime: 5 * 60 * 1000, // 5 minutes — branding rarely changes
    retry: false, // Don't retry branding failures — platform defaults are fine
  })

  // Apply branding whenever it changes
  useEffect(() => {
    if (branding) {
      applyBranding(branding)
    }
    return () => {
      clearBranding()
    }
  }, [branding])

  return branding ?? null
}
